/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { io, Socket } from "socket.io-client";
import { ConfigModule } from "@/store/modules/config";
import { UserModule } from "@/store/modules/me-user.store";
import { WssEventTypeEnum } from "./wss-event-type.enum";
import { IWssEvent, IWssConnectedPayload } from "./wss-event.interface";
import { WssEventEmitter } from "./wss-event-emitter";
import Vue from "vue";

/**
 * Configuration options for the WebSocket service
 */
export interface IWssServiceOptions {
  /**
   * The partner ID to subscribe to events for
   */
  partnerId: string;

  /**
   * Whether to enable debug logging
   */
  debug?: boolean;
}

/**
 * WebSocket service that manages Socket.IO connection to the backend WSS server.
 *
 * This service:
 * - Establishes a secure WebSocket connection using JWT authentication
 * - Automatically reconnects on disconnection
 * - Emits events through the WssEventEmitter for business logic handlers
 *
 * @example
 * ```typescript
 * // Initialize the service
 * await WssService.connect({ partnerId: "123" });
 *
 * // Listen for events
 * WssEventEmitter.on("report.created", (event) => {
 *   console.log("Report created:", event.data);
 * });
 *
 * // Disconnect when done
 * WssService.disconnect();
 * ```
 */
class WssServiceClass {
  private socket: Socket | null = null;
  private options: IWssServiceOptions | null = null;
  private logger = Vue.prototype.$log;

  /**
   * Whether the service is currently connected
   */
  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get the WebSocket base URL from config
   */
  private get baseUrl(): string {
    return ConfigModule.wssBaseUrl;
  }

  /**
   * Get the Socket.IO path from config
   */
  private get socketPath(): string {
    return ConfigModule.wssSocketPath;
  }

  /**
   * Connect to the WebSocket server.
   *
   * @param options Connection options including partnerId
   * @returns Promise that resolves when connected
   */
  async connect(options: IWssServiceOptions): Promise<void> {
    // Disconnect existing connection if any
    if (this.socket) {
      this.disconnect();
    }

    this.options = options;

    // Ensure we have a valid access token
    const accessToken = UserModule.authAccessTokens?.accessToken;
    if (!accessToken) {
      const error = new Error("No access token available for WebSocket connection");
      this.log("error", error.message);
      throw error;
    }

    return new Promise((resolve, reject) => {
      try {
        this.log("debug", `Connecting to WSS at ${this.baseUrl} with path ${this.socketPath}`);

        this.socket = io(this.baseUrl, {
          path: this.socketPath,
          auth: {
            token: accessToken
          },
          query: {
            partnerId: options.partnerId
          },
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000
        });

        this.setupEventListeners(resolve, reject);
      } catch (error) {
        this.log("error", "Failed to create socket connection", error);
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.log("debug", "Disconnecting from WSS");
      this.socket.disconnect();
      this.socket = null;
    }
    this.options = null;
  }

  /**
   * Change the partner subscription.
   * Reconnects with the new partner ID.
   *
   * @param partnerId The new partner ID to subscribe to
   */
  async switchPartner(partnerId: string): Promise<void> {
    if (!this.options) {
      throw new Error("Cannot switch partner: not connected");
    }

    this.log("debug", `Switching partner to ${partnerId}`);
    await this.connect({ ...this.options, partnerId });
  }

  /**
   * Setup Socket.IO event listeners
   */
  private setupEventListeners(onConnect: () => void, onConnectError: (error: Error) => void): void {
    if (!this.socket) return;

    // Connection established
    this.socket.on("connect", () => {
      this.log("debug", "Socket.IO connected", { socketId: this.socket?.id });
    });

    // Connected event from server (after joining partner room)
    this.socket.on(WssEventTypeEnum.CONNECTED, (payload: IWssConnectedPayload) => {
      this.log("debug", "WSS connected to partner room", payload);
      WssEventEmitter.emitConnected(payload);
      onConnect();
    });

    // Partner events from the backend
    this.socket.on(WssEventTypeEnum.PARTNER_EVENT, (event: IWssEvent) => {
      this.log("debug", "WSS partner event received", event);
      WssEventEmitter.emit(event);
    });

    // Error events
    this.socket.on(WssEventTypeEnum.ERROR, (error: Error) => {
      this.log("error", "WSS error event", error);
      WssEventEmitter.emitError(error);
    });

    // Socket.IO built-in events
    this.socket.on("connect_error", (error: Error) => {
      this.log("error", "Socket.IO connection error", error);
      WssEventEmitter.emitError(error);
      onConnectError(error);
    });

    this.socket.on("disconnect", (reason: string) => {
      this.log("debug", "Socket.IO disconnected", { reason });
      WssEventEmitter.emitDisconnected(reason);
    });

    this.socket.on("reconnect", (attemptNumber: number) => {
      this.log("debug", "Socket.IO reconnected", { attemptNumber });
    });

    this.socket.on("reconnect_attempt", (attemptNumber: number) => {
      this.log("debug", "Socket.IO reconnection attempt", { attemptNumber });
    });

    this.socket.on("reconnect_failed", () => {
      this.log("error", "Socket.IO reconnection failed");
      WssEventEmitter.emitError(new Error("WebSocket reconnection failed"));
    });
  }

  /**
   * Log a message based on the debug setting
   */
  private log(level: "debug" | "info" | "warn" | "error", message: string, data?: unknown): void {
    const prefix = "[WssService]";

    // Always log errors
    if (level === "error") {
      if (this.logger?.error) {
        this.logger.error(`${prefix} ${message}`, data);
      } else {
        console.error(`${prefix} ${message}`, data);
      }
      return;
    }

    // Only log other levels if debug is enabled
    if (!this.options?.debug && !ConfigModule.debug) {
      return;
    }

    const logFn = this.logger?.[level] || console[level];
    if (data !== undefined) {
      logFn(`${prefix} ${message}`, data);
    } else {
      logFn(`${prefix} ${message}`);
    }
  }
}

/**
 * Singleton instance of the WebSocket service.
 */
export const WssService = new WssServiceClass();
